import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { WidgetRecommendations } from "~/api/Api";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { usePageTitle } from "~/hooks/usePageTitle";

import { apiClient } from "../../../api/ApiClient";
import Interlinks, {
  PageItem,
} from "../../../components/automations/interlinks/Interlinks";
import { PaginationConfig } from "../../../components/ui/pagination/Pagination";
import styles from "./Interlinks.module.scss";

type EnhancedPageItem = Omit<PageItem, "details"> & {
  details?: WidgetRecommendations;
};

const InterlinksPage = () => {
  usePageTitle(PAGE_TITLES.interlinks);
  const { id } = useParams();
  const [allData, setAllData] = useState<EnhancedPageItem[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isWidgetActionLoading, setIsWidgetActionLoading] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<EnhancedPageItem>();
  const processedPagesRef = useRef<Record<number, boolean>>({});
  const initialPageLoadedRef = useRef<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 20,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  const allPagesLoaded =
    pagination.currentPage === pagination.totalPages ||
    pagination.totalPages === 0;

  const { data: interlinksCategory, isLoading } = useQuery({
    queryKey: ["onpage", id],
    queryFn: async () => {
      const response = await apiClient.widget.categoriesList();

      return (
        response.data?.["widget/categories"]?.find(
          (item) => item.kind === "interlink",
        ) ?? null
      );
    },
  });
  const interlinksId = interlinksCategory?.id;
  const { data: interlinksCategotyStats } = useQuery({
    queryKey: ["interlinksCategotyStats", id, interlinksId],
    queryFn: async () => {
      const response = await apiClient.sites.widgetCategoriesStatsDetail(
        Number(id),
      );

      return response.data;
    },
    enabled: !!interlinksId,
  });

  interface InterlinksStats {
    approved_appearances: number;
    appearances: number;
  }

  const interlinksStats: InterlinksStats | null = useMemo(() => {
    if (
      !interlinksCategotyStats ||
      !interlinksCategotyStats["widget/categories_stats"]
    ) {
      return null;
    }

    const categoryStats = interlinksCategotyStats["widget/categories_stats"];
    const interlinkEntry = Object.entries(categoryStats).find(
      (entry) => entry[1]?.category_name === "Interlinks",
    );

    return interlinkEntry ? (interlinkEntry[1] as InterlinksStats) : null;
  }, [interlinksCategotyStats]);

  const loadPageDetails = async (
    pageId: number,
  ): Promise<WidgetRecommendations> => {
    try {
      const detailsResponse =
        await apiClient.sites.pagesWidgetRecommendationsDetail(
          Number(id),
          pageId,
          {
            "page[size]": 100,
            q: {
              category_id_eq: interlinksId,
            },
          },
          {
            headers: {
              "Authorize-Member-Actions": "update",
              "Authorize-Collection-Actions": "update",
            },
          },
        );

      return detailsResponse?.data?.["widget/recommendations"] || [];
    } catch (error) {
      console.error(`Error fetching details for page ${pageId}:`, error);

      return [];
    }
  };

  const handleSetCurrentPage = async (page: EnhancedPageItem) => {
    if (currentPage?.id === page.id) return;
    setIsWidgetActionLoading(true);
    if (page.details && page.details.length > 0) {
      setCurrentPage(page);
      setIsWidgetActionLoading(false);

      return;
    }
    try {
      const details = await loadPageDetails(page.id);
      const enhancedPage: EnhancedPageItem = { ...page, details };

      setAllData((prevData) =>
        prevData.map((p) => (p.id === page.id ? enhancedPage : p)),
      );
      setCurrentPage(enhancedPage);

      processedPagesRef.current[page.id] = true;
    } catch (error) {
      console.error("Error setting current page:", error);
    } finally {
      setIsWidgetActionLoading(false);
    }
  };

  const {
    data,
    isLoading: isPagesLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "interlinks-pages",
      pagination.currentPage,
      interlinksId,
      searchTerm,
    ],
    queryFn: async () => {
      if (pagination.currentPage === 1) {
        setIsWidgetActionLoading(true);
      }

      const response = await apiClient.sites.pagesAutomatedDetail(
        Number(id),
        {
          "page[size]": 15,
          "page[number]": pagination.currentPage,
          q: {
            page_recommendations_category_id_eq: interlinksId,
            // path_and_query_cont: searchTerm,
          },
        },
        {
          headers: {
            "Authorize-Member-Actions": "update",
            "Authorize-Collection-Actions": "update",
          },
        },
      );
      const pages = response.data?.page_automations || [];

      const { headers } = response;
      const loading = pagination.currentPage === 1 && isLoading;
      setIsInitialLoading(loading);

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));

      if (pages.length === 0) {
        setIsWidgetActionLoading(false);

        return [];
      }

      setAllData((prevData) => {
        if (pagination.currentPage === 1) {
          return pages;
        } else {
          return [...prevData, ...pages];
        }
      });

      const needInitialPage =
        !currentPage &&
        !initialPageLoadedRef.current &&
        pagination.currentPage === 1;
      const firstPage = needInitialPage ? pages[0] : null;

      const fetchDetails = async () => {
        const pagesToProcess = pages.filter(
          (page: PageItem) => !processedPagesRef.current[page.id],
        );

        if (pagesToProcess.length === 0) {
          if (firstPage && processedPagesRef.current[firstPage.id]) {
            const pageWithDetails = allData.find((p) => p.id === firstPage?.id);
            if (pageWithDetails) {
              setCurrentPage(pageWithDetails);
              initialPageLoadedRef.current = true;
            }
          }
          setIsWidgetActionLoading(false);

          return;
        }

        let completedCount = 0;
        let initialPageLoaded = false;

        for (const page of pagesToProcess) {
          try {
            const details = await loadPageDetails(page.id);

            processedPagesRef.current[page.id] = true;

            const enhancedPage = {
              ...page,
              details,
            };

            setAllData((prevData) =>
              prevData.map((p) => (p.id === page.id ? enhancedPage : p)),
            );

            if (firstPage && firstPage.id === page.id && !initialPageLoaded) {
              setCurrentPage(enhancedPage);
              initialPageLoaded = true;
              initialPageLoadedRef.current = true;
            }
            if (currentPage && currentPage.id === page.id) {
              setCurrentPage(enhancedPage);
            }
          } catch (error) {
            console.error(`Error fetching details for page ${page.id}:`, error);
          } finally {
            completedCount++;
            if (completedCount === pagesToProcess.length) {
              setIsWidgetActionLoading(false);
            }
          }
        }
      };

      fetchDetails();
      setIsInitialLoading(false);

      return pages;
    },
    enabled: !!id && !!interlinksId,
  });

  const handleMutationSuccess = () => {
    if (!currentPage?.id) return;

    setIsWidgetActionLoading(true);

    if (currentPage?.id) {
      processedPagesRef.current[currentPage.id] = false;
    }

    const refreshCurrentPage = async () => {
      try {
        const details = await loadPageDetails(currentPage.id);

        const enhancedPage = {
          ...currentPage,
          details,
        };

        setCurrentPage(enhancedPage);

        setAllData((prevData) =>
          prevData.map((p) => (p.id === currentPage.id ? enhancedPage : p)),
        );
      } catch (error) {
        console.error("Error refreshing current page details:", error);
      } finally {
        setIsWidgetActionLoading(false);
      }
    };

    refreshCurrentPage();
  };

  const noData = data?.length === 0 && !isPagesLoading;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.interlinks}</h1>
      <Interlinks
        interlinksId={interlinksId}
        description={interlinksCategory?.description}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        noData={noData}
        // @ts-expect-error BE types
        data={allData}
        pagination={pagination}
        setPagination={setPagination}
        isInitialLoading={isInitialLoading}
        isFetching={isFetching}
        allPagesLoaded={allPagesLoaded}
        // @ts-expect-error BE types
        currentPage={currentPage}
        // @ts-expect-error BE types
        setCurrentPage={handleSetCurrentPage}
        isWidgetActionLoading={isWidgetActionLoading}
        onMutationSuccess={handleMutationSuccess}
        approvedCount={
          interlinksStats ? interlinksStats.approved_appearances : 0
        }
        totalCount={interlinksStats ? interlinksStats?.appearances : 0}
      />
    </div>
  );
};

export default InterlinksPage;
